import React, {useState} from 'react';
import {AlertTriangleIcon, CheckCheckIcon, ChevronDownIcon} from './icons';
import {OutfitScoreDiagnostic} from './OutfitScoreBadge';
import {cautionsFromDiagnostics, reasonsFromDiagnostics} from '../support/outfitScoreReasons';

interface Props {
    /** Диагностики движка, уже отсортированы по |contribution|; показываются топ-3. */
    diagnostics: OutfitScoreDiagnostic[];
    /** Подсказки минимальных изменений (замены из counterfactuals/replacements). */
    hints?: string[];
    /**
     * Причины оценки человеческим языком с бэкенда (`OutfitScoreResultDto.reasons`).
     * Не передали — выводятся из диагностик теми же формулировками.
     */
    reasons?: string[];
    /** Конфликты образа человеческим языком (`OutfitScoreResultDto.cautions`). */
    cautions?: string[];
    defaultOpen?: boolean;
    className?: string;
}

const MAX_DIAGNOSTICS = 3;

const severityIcon = (severity: string) => {
    if (severity === 'REINFORCING') {
        return <CheckCheckIcon className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5"/>;
    }
    const tone = severity === 'MAJOR' || severity === 'INVALID' ? 'text-red-500' : 'text-amber-500';
    return <AlertTriangleIcon className={`w-3.5 h-3.5 ${tone} shrink-0 mt-0.5`}/>;
};

const contributionLabel = (value: number | null | undefined): string | null => {
    if (value == null || value === 0) return null;
    const rounded = Math.round(value * 10) / 10;
    return rounded > 0 ? `+${rounded}` : `${rounded}`;
};

/**
 * Объяснение оценки образа (outfit-score-engine FR-3/BR-3; UX-аудит 16.09, F09).
 *
 * Сверху — 2–3 причины человеческим языком («Единый стиль», «Цвета из одной палитры») и
 * конфликты, без единого числа. Технические слагаемые («Формальность вещей выровнена: 0.98
 * +26.4») ушли под раскрытие «Детали расчёта»: аудит показал, что как объяснение оценки они
 * не читаются. Вторичная кнопка-переключатель — primary-кнопка региона остаётся за основным
 * действием страницы.
 */
const OutfitScoreDetails: React.FC<Props> = ({
    diagnostics, hints = [], reasons, cautions, defaultOpen = false, className = '',
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const top = diagnostics.slice(0, MAX_DIAGNOSTICS);
    const shownReasons = reasons ?? reasonsFromDiagnostics(diagnostics);
    const shownCautions = cautions ?? cautionsFromDiagnostics(diagnostics);
    if (top.length === 0 && hints.length === 0 && shownReasons.length === 0 && shownCautions.length === 0) {
        return null;
    }
    return (
        <div className={className} data-testid="outfit-score-details">
            {(shownReasons.length > 0 || shownCautions.length > 0) && (
                <ul className="space-y-1.5 mb-1" data-testid="outfit-score-reasons">
                    {shownReasons.map(r => (
                        <li key={r} className="flex gap-2 text-xs" data-testid="outfit-score-reason">
                            <CheckCheckIcon className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5"/>
                            <span className="text-gray-600 dark:text-gray-300">{r}</span>
                        </li>
                    ))}
                    {shownCautions.map(c => (
                        <li key={c} className="flex gap-2 text-xs" data-testid="outfit-score-caution">
                            <AlertTriangleIcon className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5"/>
                            <span className="text-amber-600 dark:text-amber-400">{c}</span>
                        </li>
                    ))}
                </ul>
            )}
            <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
                    data-testid="outfit-score-details-toggle"
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors min-h-[44px] sm:min-h-0 py-1">
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}/>
                Детали расчёта
            </button>
            {open && (
                <div className="mt-1.5 space-y-2" data-testid="outfit-score-details-body">
                    <ul className="space-y-1.5">
                        {top.map((d, i) => {
                            const contribution = contributionLabel(d.contribution);
                            return (
                                <li key={`${d.code}-${i}`} className="flex gap-2 text-xs" data-testid="outfit-score-diagnostic">
                                    {severityIcon(d.severity)}
                                    <span className="text-gray-600 dark:text-gray-300">
                                        {d.message}
                                        {contribution && (
                                            <span className={`ml-1 font-medium ${contribution.startsWith('+') ? 'text-green-500' : 'text-red-500'}`}>
                                                {contribution}
                                            </span>
                                        )}
                                    </span>
                                </li>
                            );
                        })}
                        {top.length === 0 && (
                            <li className="text-xs text-gray-400">Заметных конфликтов не найдено.</li>
                        )}
                    </ul>
                    {hints.length > 0 && (
                        <div data-testid="outfit-score-hints">
                            <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                                Что минимально изменить
                            </p>
                            <ul className="space-y-1">
                                {hints.map((h, i) => (
                                    <li key={i} className="text-xs text-gray-600 dark:text-gray-300">{h}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OutfitScoreDetails;
